import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Package, Clock, MapPin, Eye, Wifi } from 'lucide-react'
import { getUserOrders, SecretOrder } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { useUserOrdersRealtime } from '../hooks/useOrderRealtime'
import { formatPrice } from '../lib/square'
import OrderStatusIndicator from '../components/orders/OrderStatusIndicator'
import LoadingSpinner from '../components/ui/LoadingSpinner'

interface OrderWithEvent extends SecretOrder {
  secret_events: {
    title: string
    location_name: string
    event_date: string
  }
}

const OrdersPage: React.FC = () => {
  const { user } = useAuth()
  const [orders, setOrders] = useState<OrderWithEvent[]>([])
  const [loading, setLoading] = useState(true)

  // Real-time updates for all user orders
  const { isConnected: isRealtimeConnected, lastUpdate } = useUserOrdersRealtime(
    user?.id || '',
    {
      onStatusChange: (change) => {
        // Update specific order in the list
        setOrders(prev => 
          prev.map(order => 
            order.id === change.orderId 
              ? { ...order, ...change.order }
              : order
          )
        )
      },
      enableNotifications: false, // Disable individual notifications since we show toasts
      enableToasts: true
    }
  )

  useEffect(() => {
    const loadOrders = async () => {
      console.log('[OrdersPage] useEffect triggered. user?.id:', user?.id)

      if (!user?.id) {
        console.log('[OrdersPage] No user.id, skipping load')
        return
      }

      try {
        console.log('[OrdersPage] Loading orders for user:', user.id)
        setLoading(true)
        const userOrders = await getUserOrders(user.id) as OrderWithEvent[]
        console.log('[OrdersPage] Loaded orders:', userOrders.length)
        setOrders(userOrders)
      } catch (error) {
        console.error('[OrdersPage] Failed to load orders:', error)
      } finally {
        setLoading(false)
      }
    }

    loadOrders()
  }, [user?.id])

  const formatEventDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-black text-secret-text-primary">My Orders</h1>
          <p className="text-secret-text-secondary mt-2">
            Track your SECRET pre-orders and pickup status
          </p>
        </div>
        
        {/* Real-time Status */}
        {orders.length > 0 && (
          <div className="flex items-center space-x-2 text-sm">
            {isRealtimeConnected ? (
              <>
                <Wifi className="w-4 h-4 text-green-400" />
                <span className="text-green-400">Live Updates</span>
              </>
            ) : (
              <>
                <div className="w-4 h-4 rounded-full bg-gray-500" />
                <span className="text-secret-text-secondary">Offline</span>
              </>
            )}
          </div>
        )}
      </div>
      
      {orders.length === 0 ? (
        /* Empty State */
        <div className="secret-card text-center py-16">
          <Package className="w-20 h-20 text-secret-text-tertiary mx-auto mb-6" />
          <h2 className="text-xl font-bold text-secret-text-primary mb-4">No Orders Yet</h2>
          <p className="text-secret-text-secondary max-w-md mx-auto mb-6">
            Your SECRET pre-orders will appear here once you place them.
          </p>
          <Link
            to="/events"
            className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-[#962831] text-white font-medium transition-colors hover:opacity-90"
          >
            <span className="text-white">Browse Events</span>
          </Link>
        </div>
      ) : (
        /* Orders List */
        <div className="space-y-6">
          {orders.map((order) => (
            <div key={order.id} className="secret-card">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                <div className="flex-1 space-y-4 lg:space-y-0 lg:grid lg:grid-cols-3 lg:gap-6">
                  {/* Order Info */}
                  <div>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-bold text-secret-text-primary">#{order.order_number}</h3>
                        <p className="text-secret-text-secondary text-sm">
                          {formatEventDate(order.created_at)}
                        </p>
                      </div>
                      <div className="text-secret-primary font-bold">
                        {formatPrice(order.total_amount * 100)}
                      </div>
                    </div>
                    
                    {/* Event Details */}
                    <div className="flex items-start space-x-2 text-sm">
                      <MapPin className="w-4 h-4 text-secret-text-tertiary mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-secret-text-primary font-medium">{order.secret_events.title}</div>
                        <div className="text-secret-text-secondary">{order.secret_events.location_name}</div>
                        <div className="text-secret-text-tertiary">
                          {formatEventDate(order.secret_events.event_date)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Status */}
                  <div className="flex justify-center">
                    <OrderStatusIndicator
                      status={order.status}
                      isRealtime={isRealtimeConnected}
                      lastUpdate={lastUpdate}
                      showLabel={true}
                      size="sm"
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end space-x-3">
                    <Link
                      to={`/orders/${order.id}`}
                      className="secret-btn-secondary flex items-center space-x-1"
                    >
                      <Eye size={14} />
                      <span>View Details</span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
          
          {/* Summary */}
          <div className="secret-card bg-secret-bg-tertiary">
            <div className="text-center">
              <h3 className="font-bold text-secret-text-primary mb-2">Order Summary</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="text-secret-text-secondary">Total Orders</div>
                  <div className="text-secret-text-primary font-bold">{orders.length}</div>
                </div>
                <div>
                  <div className="text-secret-text-secondary">Active</div>
                  <div className="text-secret-text-primary font-bold">
                    {orders.filter(o => ['confirmed', 'preparing', 'ready'].includes(o.status)).length}
                  </div>
                </div>
                <div>
                  <div className="text-secret-text-secondary">Completed</div>
                  <div className="text-secret-text-primary font-bold">
                    {orders.filter(o => o.status === 'completed').length}
                  </div>
                </div>
                <div>
                  <div className="text-secret-text-secondary">Total Spent</div>
                  <div className="text-secret-primary font-bold">
                    {formatPrice(orders.reduce((sum, o) => sum + (o.status !== 'cancelled' ? o.total_amount : 0), 0) * 100)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default OrdersPage